import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { BaseViewComponent, RangeLabelValue } from '@sb/library/base';
import { LocalizationInfo } from '@sb/library/data-models';
import { LocalizationService, SBDate } from '@sb/library/localization';
import { MbscDateType, MbscDatepicker, MbscDatepickerChangeEvent, MbscDatepickerCloseEvent } from '@mobiscroll/angular';

@Component({
    selector: 'lib-date-range-selector',
    templateUrl: './date-range-selector.component.html',
    styleUrls: ['./date-range-selector.component.scss']
})
export class DateRangeSelectorComponent extends BaseViewComponent implements OnInit {

    @ViewChild(MbscDatepicker, {static: true}) datePicker!: MbscDatepicker;

    @Input()
    set initialDate(value: { StartDate: SBDate, EndDate: SBDate }) {
        if (!value) {
            return;
        }
        this._initialDate = value;
        this._startDate = value.StartDate;
        this._endDate = value.EndDate;
        this.UpdateDatePicker();
    }

    private _defaultMinDateJS: Date = SBDate.FromISO('2013-01-01').ToJSDate(); // all users can go back to start of MMS creation
    protected _minDateJS: Date = this._defaultMinDateJS;
    @Input()
    set minDate(value: SBDate | null) {
        this._minDateJS = value ? value.ToJSDate() : this._defaultMinDateJS;
    }

    private _defaultMaxDateJS: Date = SBDate.Today().AddYears(2).ToJSDate(); //initial value to be overrideen later
    private _maxDateJS: Date = this._defaultMaxDateJS;
    @Input()
    set maxDate(value: SBDate | null) {
        this._maxDateJS = value ? value.ToJSDate() : this._defaultMaxDateJS;
    }

    @Input() showRangeLabels: boolean = true;

    @Output() change: EventEmitter<{ StartDate: SBDate, EndDate: SBDate }> = new EventEmitter();

    private _startDate: SBDate = SBDate.StartOfMonth();
    private _endDate: SBDate = this._startDate.Clone().AddMonths(1).AddDays(-1);
    private _initialDate?: { StartDate: SBDate, EndDate: SBDate };
    private _localeInfo!: LocalizationInfo;

    constructor(private _LocalizationService: LocalizationService) {
        super();
        this._localeInfo = this._LocalizationService.LocalizationInfo;

    }

    override ngOnInit(): void {
        super.ngOnInit();
        if (this._initialDate === undefined) {
            this.UpdateDatePicker();
            this.EmitDate();
        }
    }

    DateChangeEvent(change: MbscDatepickerChangeEvent) {
        const value = change.value as MbscDateType[];
        const oldStartDate = this._startDate;
        const oldEndDate = this._endDate;
        if (change.valueText?.includes('-')) { // we have both dates
            this._startDate = SBDate.FromJSDate(value[0] as Date);
            this._endDate = SBDate.FromJSDate(value[1] as Date);
            if (oldStartDate.totalMilliseconds != this._startDate.totalMilliseconds || oldEndDate.totalMilliseconds != this._endDate.totalMilliseconds) {
                this.EmitDate();
            }
        }
    }

    DatePickerCloseEvent(change: MbscDatepickerCloseEvent) {
        // if end date is not selected, default to 1 month after the start date.
        const value = change.value as MbscDateType[];
        if (!value[1]) {
            this._startDate = SBDate.FromJSDate(value[0] as Date);
            this._endDate = this._startDate.Clone().AddMonths(1);
        }
    }

    OpenDatePicker(rangeLabelOnOpen: RangeLabelValue): void {
        this.datePicker.open();
        setTimeout(() => {
            this.datePicker.setActiveDate(rangeLabelOnOpen);
        })
    }

    private UpdateDatePicker(): void {
        this.datePicker.setVal([this._startDate.ToISO(), this._endDate.ToISO()]);
    }

    private EmitDate(): void {
        this.change.emit({StartDate: this._startDate, EndDate: this._endDate});
    }

    get startDate(): string {
        return this._startDate.Render('iso');
    }

    get endDate(): string {
        return this._endDate.Render('iso');
    }

    get dateFormat() {
        return this._LocalizationService.ShortDatePattern;
    }

    get minDateJS(): Date {
        return this._minDateJS;
    }

    get maxDateJS(): Date {
        return this._maxDateJS;
    }

    get localeInfo(): LocalizationInfo {
        return this._localeInfo;
    }

    get firstDayOfWeek(): number {
        return this._LocalizationService.firstDayOfWeekIndex;
    }

    get RangeLabelValue(): typeof RangeLabelValue {
        return RangeLabelValue;
    }
}
